import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  TextField,
  Alert,
} from '@mui/material';
import { Flag as FlagIcon } from '@mui/icons-material';
import { apiCall } from '../../utils/fetchAPI';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import { REPORT_REASONS } from '../../constants/community';
import { translateApiMessage } from '../../utils/apiMessages';

/**
 * Flag a piece of content for a moderator.
 *
 * One dialog for every kind of content — a post, a comment, an event — with
 * the kind in the payload, which sends the report to that kind's queue. The
 * reporter is told their report was sent and nothing more: what a moderator
 * decides is not theirs to see.
 */
function ReportDialog({ targetType, targetUuid, label = 'Signaler' }) {
  const { isAuthenticated } = useAuth();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [comment, setComment] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  if (!isAuthenticated) {
    return null;
  }

  const close = () => {
    setOpen(false);
    setError('');
  };

  const submit = async (event) => {
    event.preventDefault();

    if (!reason) {
      setError('Choisissez un motif.');
      return;
    }

    setSending(true);
    setError('');

    try {
      const response = await apiCall('/reports', {
        method: 'POST',
        body: JSON.stringify({
          type: targetType,
          uuid: targetUuid,
          reason,
          comment: comment.trim() || null,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        setError(translateApiMessage(data.error) || 'Votre signalement n’a pas pu être envoyé.');
        return;
      }

      toast.success(data.message || 'Merci, votre signalement a été transmis à la modération.');
      setReason('');
      setComment('');
      close();
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Deliberately quiet: reporting is available everywhere, but it is
          never what a page is for. */}
      <Button
        size="small"
        variant="text"
        startIcon={<FlagIcon fontSize="small" />}
        onClick={() => setOpen(true)}
        sx={{ color: 'text.disabled', textDecoration: 'none', '&:hover': { color: 'error.main' } }}
      >
        {label}
      </Button>

      <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
        <form onSubmit={submit}>
          <DialogTitle>Signaler ce contenu</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 3 }}>
              Un modérateur le lira. Vous ne serez pas informé de la décision, et l’auteur
              ne saura pas qui l’a signalé.
            </DialogContentText>

            <TextField
              select
              label="Motif"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              fullWidth
              required
              disabled={sending}
              sx={{ mb: 2.5 }}
            >
              {REPORT_REASONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Précision (facultatif)"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              multiline
              minRows={3}
              fullWidth
              disabled={sending}
              inputProps={{ maxLength: 1000 }}
            />

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={close} disabled={sending} variant="outlined">
              Annuler
            </Button>
            <Button type="submit" variant="contained" disabled={sending}>
              Envoyer le signalement
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
}

export default ReportDialog;
